import React, { FC, memo, useEffect, useMemo, useState } from 'react';
import styles from './google-maps-stores.module.css';
import { APIProvider, InfoWindow, Map, MapCameraProps } from '@vis.gl/react-google-maps';
import { StoreSearchViewModel } from '~/lib/data-contract';
import { Button, Typography } from '~/shared/components';
import Markers from './markers.component';
import { useTranslation } from '~/shared/utils';
import { getOpeningHourForToday } from '../p170-store-overview-page.component';

const noResultsMapCamera = {
    center: {
        lat: 56.61083944739298,
        lng: 15.226864848263432,
    },
    zoom: 4.8,
};

function getCenter(stores: StoreSearchViewModel[]): { lat: number; lng: number } {
    if (stores.length === 0) return noResultsMapCamera.center;
    const sum = stores.reduce(
        (acc, store) => {
            acc.lat += store.latitude || 0;
            acc.lng += store.longitude || 0;
            return acc;
        },
        { lat: 0, lng: 0 }
    );
    return {
        lat: sum.lat / stores.length,
        lng: sum.lng / stores.length,
    };
}

function getZoom(stores: StoreSearchViewModel[]): number {
    if (stores.length === 0) return noResultsMapCamera.zoom;
    if (stores.length === 1) return 14;

    // Calculate bounding box manually
    const validStores = stores.filter(
        (s) => typeof s.latitude === 'number' && typeof s.longitude === 'number'
    );
    if (validStores.length === 0) return 7;

    const lats = validStores.map((s) => s.latitude as number);
    const lngs = validStores.map((s) => s.longitude as number);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);

    if (maxDiff < 0.01) return 13.7;
    if (maxDiff < 0.05) return 12.7;
    if (maxDiff < 0.1) return 11.7;
    if (maxDiff < 0.5) return 10.7;
    if (maxDiff < 1) return 8.7;
    return 6.7;
}

type Props = {
    stores: StoreSearchViewModel[];
    searchQuery?: string;
};

const GoogleMapsStores: FC<Props> = memo((props) => {
    const { stores, searchQuery } = props;
    const calculatedCenter = useMemo(() => getCenter(stores), [stores]);
    const calculatedZoom = useMemo(() => getZoom(stores), [stores]);

    const [cameraView, setCameraView] = useState<Pick<MapCameraProps, 'center' | 'zoom'>>({
        center: calculatedCenter,
        zoom: calculatedZoom,
    });

    const [selectedStore, setSelectedStore] = useState<StoreSearchViewModel | null>(null);

    const { translate } = useTranslation();

    const openingHours = selectedStore ? getOpeningHourForToday(selectedStore) : null;

    useEffect(() => {
        setCameraView({
            center: calculatedCenter,
            zoom: calculatedZoom,
        });
    }, [calculatedCenter, calculatedZoom]);

    return (
        <div className={styles.mapRoot}>
            <APIProvider apiKey="AIzaSyBFE8k1SFuAny1IVuCQi_dXTGvylx_yetQ">
                <Map
                    mapId="5b526e9924c59925313e321d"
                    // defaultCenter={center}
                    // defaultZoom={zoomLevel}
                    center={cameraView.center}
                    zoom={searchQuery && searchQuery.length > 2 ? 10 : cameraView.zoom}
                    onCameraChanged={(newCamera) => {
                        setCameraView(newCamera.detail);
                    }}
                    disableDefaultUI
                    colorScheme="LIGHT"
                >
                    <Markers stores={stores} setSelectedStore={setSelectedStore} />
                    {selectedStore && (
                        <InfoWindow
                            position={{
                                lat: selectedStore.latitude ?? 0,
                                lng: selectedStore.longitude ?? 0,
                            }}
                            onCloseClick={() => setSelectedStore(null)}
                            onClose={() => setSelectedStore(null)}
                            headerContent={
                                <Typography variant="bodySm" className={styles.infoWindowHeadline}>
                                    {selectedStore.storeName}
                                </Typography>
                            }
                        >
                            <div className={styles.infoWindow}>
                                <Typography variant="bodyXs" className={styles.infoText}>
                                    {selectedStore.address}, {selectedStore.zipcode}{' '}
                                    {selectedStore.city}
                                </Typography>
                                {openingHours?.isClosed ? (
                                    <Typography variant="bodyXs" className={styles.infoText}>
                                        {translate('stores.overview.openingHoursClosed')}
                                    </Typography>
                                ) : (
                                    <Typography variant="bodyXs" className={styles.infoText}>
                                        {translate('stores.overview.openingHoursOpen')}:{' '}
                                        {openingHours?.openingTime} - {openingHours?.closingTime}
                                    </Typography>
                                )}
                                <Button
                                    size="small"
                                    hoverEffect
                                    href={selectedStore?.url}
                                    className={styles.infoWindowButton}
                                >
                                    {translate('stores.overview.storeLinkText')}
                                </Button>
                            </div>
                        </InfoWindow>
                    )}
                </Map>
            </APIProvider>
        </div>
    );
});

GoogleMapsStores.displayName = 'GoogleMapsStores';
export default GoogleMapsStores;
